import { demoCrops, demoDashboard, demoPosts, demoUser } from "../data/fallbackData";

const API_ROOT = process.env.REACT_APP_API_URL || "/api";

const fallbackEnabled = process.env.NODE_ENV === "development";

const getDemoCreatedCrops = () => JSON.parse(window.localStorage.getItem("demo-created-crops") || "[]");
const getDemoDeletedCropIds = () => JSON.parse(window.localStorage.getItem("demo-deleted-crops") || "[]");

async function request(path, options = {}) {
  const response = await fetch(`${API_ROOT}${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
  });

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    throw new Error("API가 JSON 응답을 반환하지 않았습니다.");
  }

  const payload = await response.json();
  if (!response.ok || payload.ok === false) {
    throw new Error(payload.message || "요청을 처리하지 못했습니다.");
  }

  return payload.data;
}

const withFallback = async (action, fallback) => {
  try {
    return await action();
  } catch (error) {
    if (!fallbackEnabled) throw error;
    return typeof fallback === "function" ? fallback(error) : fallback;
  }
};

export const api = {
  getSession: () => withFallback(() => request("/auth/me.php"), () => {
    const active = window.localStorage.getItem("demo-session") === "active";
    const storedUser = JSON.parse(window.localStorage.getItem("demo-user") || "null");
    return active ? { user: storedUser || demoUser, csrfToken: "demo-token" } : { user: null, csrfToken: "demo-token" };
  }),
  login: (survivorId, password) => withFallback(
    () => request("/auth/login.php", { method: "POST", body: JSON.stringify({ survivorId, password }) }),
    () => {
      const credentials = JSON.parse(window.localStorage.getItem("demo-credentials") || "null");
      const isDefault = survivorId === "S-04112" && password === "password";
      const isRegistered = credentials?.survivorId === survivorId && credentials?.password === password;
      if (!isDefault && !isRegistered) {
        throw new Error("생존자 ID 또는 암호가 일치하지 않습니다.");
      }
      window.localStorage.setItem("demo-session", "active");
      const storedUser = JSON.parse(window.localStorage.getItem("demo-user") || "null");
      return { user: isRegistered ? storedUser : demoUser, csrfToken: "demo-token" };
    }
  ),
  register: (registration, csrfToken) => withFallback(
    () => request("/auth/register.php", { method: "POST", headers: { "X-CSRF-Token": csrfToken }, body: JSON.stringify(registration) }),
    () => {
      const user = {
        id: Date.now(), survivorId: registration.survivorId, name: registration.name,
        role: registration.role, camp: registration.camp, clearance: 1,
        condition: "정상", bloodType: registration.bloodType, lastSeenAt: "방금 전",
      };
      window.localStorage.setItem("demo-user", JSON.stringify(user));
      window.localStorage.setItem("demo-credentials", JSON.stringify({ survivorId: registration.survivorId, password: registration.password }));
      window.localStorage.setItem("demo-session", "active");
      return { user, csrfToken: "demo-token" };
    }
  ),
  logout: (csrfToken) => withFallback(
    () => request("/auth/logout.php", { method: "POST", headers: { "X-CSRF-Token": csrfToken } }),
    () => {
      window.localStorage.removeItem("demo-session");
      return null;
    }
  ),
  getDashboard: () => withFallback(() => request("/dashboard.php"), demoDashboard),
  getNews: (id) => withFallback(() => request(`/news.php${id ? `?id=${id}` : ""}`), id ? demoDashboard.news.find((item) => item.id === Number(id)) : demoDashboard.news),
  getCrops: (id) => withFallback(
    () => request(`/crops.php${id ? `?id=${id}` : ""}`),
    () => {
      const deletedIds = getDemoDeletedCropIds();
      const crops = [...getDemoCreatedCrops(), ...demoCrops].filter((item) => !deletedIds.includes(Number(item.id)));
      return id ? crops.find((item) => item.id === Number(id)) : crops;
    }
  ),
  createCrop: (crop, csrfToken) => withFallback(
    () => request("/crops.php", { method: "POST", headers: { "X-CSRF-Token": csrfToken }, body: JSON.stringify(crop) }),
    () => {
      const created = { id: Date.now(), ...crop, level: Number(crop.level), encounterCount: Number(crop.encounterCount) };
      window.localStorage.setItem("demo-created-crops", JSON.stringify([created, ...getDemoCreatedCrops()]));
      return created;
    }
  ),
  deleteCrop: (id, csrfToken) => withFallback(
    () => request(`/crops.php?id=${id}`, { method: "DELETE", headers: { "X-CSRF-Token": csrfToken } }),
    () => {
      const cropId = Number(id);
      window.localStorage.setItem("demo-created-crops", JSON.stringify(getDemoCreatedCrops().filter((item) => Number(item.id) !== cropId)));
      window.localStorage.setItem("demo-deleted-crops", JSON.stringify([...new Set([...getDemoDeletedCropIds(), cropId])]));
      return { id: cropId };
    }
  ),
  getPosts: () => withFallback(() => request("/posts.php"), demoPosts),
  createPost: (post, csrfToken) => withFallback(
    () => request("/posts.php", { method: "POST", headers: { "X-CSRF-Token": csrfToken }, body: JSON.stringify(post) }),
    () => ({ id: Date.now(), ...post, author: demoUser.name, createdAt: "방금 전" })
  ),
};
